import { useMemo, useState } from "react";
import moment from "moment";
import {
  OrderType,
  TrendPeriod,
  useGetAccountsQuery,
  useGetActivityYearsQuery,
  useGetCategoriesQuery,
  useGetFinancesHistoryQuery,
  useGetFinancesQuery,
  useGetIncomeExpensesTrendQuery,
  useGetInvestmentsQuery,
  useGetTopExpenseCategoriesQuery,
  useGetTransactionsQuery,
  useGetYearOverYearQuery,
} from "../../services";
import {
  Series,
  HorizontalBarChartDataPoint,
  currencyFormatterNull,
} from "../../components/Charts";
import { GRAPH_COLORS } from "../../utils/constants";
import { TransactionType, localToday } from "../../utils";
import { SortValueType } from "../Transactions/types";
import { getCardAndChartData } from "./utils";
import { CardProps, UseFinancesDataResult, YearTotals } from "./types";

const RECENT_TRANSACTIONS_LIMIT = 5;
const TOP_EXPENSES_LIMIT = 5;

export const useFinancesData = (): UseFinancesDataResult => {
  const [trendPeriod, setTrendPeriod] = useState<TrendPeriod>("all-time");

  const financesQuery = useGetFinancesQuery();
  const yearFinancesQuery = useGetFinancesQuery(localToday().getFullYear());
  const historyQuery = useGetFinancesHistoryQuery();
  const trendQuery = useGetIncomeExpensesTrendQuery(trendPeriod);
  const topExpensesQuery = useGetTopExpenseCategoriesQuery({
    limit: TOP_EXPENSES_LIMIT,
  });
  const yoyQuery = useGetYearOverYearQuery();
  const availableYearsQuery = useGetActivityYearsQuery();
  const accountsQuery = useGetAccountsQuery();
  const investmentsQuery = useGetInvestmentsQuery({ activeOnly: true });
  const recentTransactionsQuery = useGetTransactionsQuery({
    sortBy: SortValueType.TRANSACTION_DATE,
    orderBy: OrderType.DESCENDING,
    page: 0,
    rowsPerPage: RECENT_TRANSACTIONS_LIMIT,
    type: [TransactionType.ALL],
    searchBy: "",
  });
  const incomeCategoriesQuery = useGetCategoriesQuery(true);
  const expenseCategoriesQuery = useGetCategoriesQuery(false);

  const { data: finances } = financesQuery;
  const { data: yearFinances } = yearFinancesQuery;
  const { data: history, isFetching: historyLoading } = historyQuery;
  const { data: trend, isFetching: trendLoading } = trendQuery;
  const { data: topExpenses, isFetching: topExpensesLoading } =
    topExpensesQuery;
  const { data: yoy } = yoyQuery;
  const { data: availableYears = [] } = availableYearsQuery;
  const { data: accounts = [] } = accountsQuery;
  const { data: investmentsData } = investmentsQuery;
  const { data: recentTransactionsResult } = recentTransactionsQuery;
  const { data: categoriesIncome = [] } = incomeCategoriesQuery;
  const { data: categoriesExpense = [] } = expenseCategoriesQuery;

  const pageQueries = [
    financesQuery,
    yearFinancesQuery,
    accountsQuery,
    investmentsQuery,
    incomeCategoriesQuery,
    expenseCategoriesQuery,
  ];
  // Secondary widgets degrade on their own; only a page read with nothing cached fails the dashboard.
  const failedQueries = pageQueries.filter(
    (query) => query.isError && query.data === undefined,
  );

  const { cards, hasData } = useMemo<{
    cards: CardProps[];
    hasData: boolean;
  }>(() => {
    if (!finances) {
      return { cards: [], hasData: false };
    }
    const { tempCards, hasData: computedHasData } =
      getCardAndChartData(finances);
    return { cards: tempCards, hasData: computedHasData };
  }, [finances]);

  const { timelineSeries, timelineLabels } = useMemo(() => {
    const points = history?.data ?? [];
    const labels = points.map((point) => moment(point.date).format("MMM YYYY"));
    const series: Series[] = [
      {
        data: points.map((point) => point.assets),
        connectNulls: true,
        area: true,
        valueFormatter: currencyFormatterNull,
        label: "Assets",
        showMark: false,
        color: GRAPH_COLORS[3],
      },
      {
        data: points.map((point) => point.liabilities),
        connectNulls: true,
        area: true,
        valueFormatter: currencyFormatterNull,
        label: "Liabilities",
        showMark: false,
        color: GRAPH_COLORS[8],
      },
      {
        data: points.map((point) => point.netWorth),
        connectNulls: true,
        valueFormatter: currencyFormatterNull,
        label: "Net Worth",
        showMark: false,
        color: GRAPH_COLORS[1],
      },
    ];
    return { timelineSeries: series, timelineLabels: labels };
  }, [history]);

  const { incomeTrendSeries, incomeTrendLabels } = useMemo(() => {
    const points = trend?.data ?? [];
    const labels = points.map((point) => point.month);
    const series: Series[] = [
      {
        data: points.map((point) => point.income),
        connectNulls: true,
        valueFormatter: currencyFormatterNull,
        label: "Income",
        showMark: false,
        color: GRAPH_COLORS[3],
      },
      {
        data: points.map((point) => point.expenses),
        connectNulls: true,
        valueFormatter: currencyFormatterNull,
        label: "Expenses",
        showMark: false,
        color: GRAPH_COLORS[2],
      },
    ];
    return { incomeTrendSeries: series, incomeTrendLabels: labels };
  }, [trend]);

  const topExpensesChartData = useMemo<HorizontalBarChartDataPoint[]>(
    () =>
      (topExpenses?.categories ?? []).map((category, index) => ({
        label: category.name,
        value: category.amount,
        color: GRAPH_COLORS[index % GRAPH_COLORS.length],
      })),
    [topExpenses],
  );

  const yearTotals = useMemo<YearTotals | null>(
    () =>
      yearFinances
        ? { income: yearFinances.income, expenses: yearFinances.expenses }
        : null,
    [yearFinances],
  );

  const investments = investmentsData?.investments ?? [];
  const recentTransactions = recentTransactionsResult?.transactions ?? [];

  const isLoading = pageQueries.some((query) => query.isLoading);

  const isFirstRun = !accounts.length && !investments.length && !hasData;

  return {
    isLoading,
    error: failedQueries.length > 0,
    retry: async () => {
      await Promise.all(failedQueries.map((query) => query.refetch()));
    },
    isFirstRun,
    cards,
    yoy: yoy ?? null,
    hasManualInvestmentAccounts: finances?.hasManualInvestmentAccounts ?? false,
    valuation: finances?.valuation ?? null,
    yearTotals,
    historyLoading,
    timelineSeries,
    timelineLabels,
    trendLoading,
    incomeTrendSeries,
    incomeTrendLabels,
    onFetchTrend: setTrendPeriod,
    topExpensesLoading,
    topExpensesChartData,
    availableYears,
    recentTransactions,
    accounts,
    categoriesIncome,
    categoriesExpense,
  };
};
